'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useEffectEvent, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { carregarPaginaDaSecaoDoCertificado } from '../store/actions';
import { selectCarregamentoDasSecoesDoCertificado, selectPaginaDoCertificadoEmVoo, selectProximaPaginaDoCertificado, selectSecoesDoDocumentoDoCertificado } from '../store/selectors';

/** Começa a pedir a próxima página cerca de uma folha A4 antes de a pessoa chegar ao fim. */
const ANTECEDENCIA_DA_SENTINELA = '0px 0px 1200px 0px';

/**
 * As seções do documento da emissão exibida, pedidas por página conforme a pessoa se aproxima do
 * fim do documento (ACC-02). A sentinela fica no fim do que já foi montado; quando ela entra na
 * área visível, sai o pedido da página seguinte, na ordem do documento.
 *
 * O observer é refeito a cada página nova (`paginaPendente` muda), porque ele só avisa quando a
 * interseção MUDA: com um documento curto, a sentinela continua visível depois da página chegar,
 * e só a observação nova entrega a interseção corrente. Sem `IntersectionObserver`, as páginas
 * vêm uma depois da outra.
 */
export function useSecoesDoCertificado(casoId, emissaoId) {
  const $ = _c(26);
  const dispatch = useAppDispatch();
  const emissao = emissaoId ?? "";
  let t0;
  if ($[0] !== emissao) {
    t0 = state => selectSecoesDoDocumentoDoCertificado(state, emissao);
    $[0] = emissao;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const documento = useAppSelector(t0);
  let t1;
  if ($[2] !== emissao) {
    t1 = state_0 => selectProximaPaginaDoCertificado(state_0, emissao);
    $[2] = emissao;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const proxima = useAppSelector(t1);
  let t2;
  if ($[4] !== emissao) {
    t2 = state_1 => selectCarregamentoDasSecoesDoCertificado(state_1, emissao);
    $[4] = emissao;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const carregamento = useAppSelector(t2);
  const emVoo = useAppSelector(selectPaginaDoCertificadoEmVoo);
  const [sentinela, setSentinela] = useState(null);
  const paginaPendente = emissaoId && proxima ? `${emissaoId}:${proxima.secao}:${proxima.cursor ?? ""}` : null;
  let t3;
  if ($[6] !== casoId || $[7] !== dispatch || $[8] !== emissaoId || $[9] !== proxima) {
    t3 = () => {
      if (!emissaoId || !proxima) {
        return;
      }
      dispatch(carregarPaginaDaSecaoDoCertificado({
        casoId,
        emissaoId,
        ...proxima
      }));
    };
    $[6] = casoId;
    $[7] = dispatch;
    $[8] = emissaoId;
    $[9] = proxima;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const pedirProximaPagina = t3;
  let t4;
  if ($[11] !== carregamento || $[12] !== emVoo || $[13] !== pedirProximaPagina) {
    t4 = () => {
      if (emVoo || carregamento === "falhou") {
        return;
      }
      pedirProximaPagina();
    };
    $[11] = carregamento;
    $[12] = emVoo;
    $[13] = pedirProximaPagina;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const pedirAoAproximar = useEffectEvent(t4);
  let t5;
  if ($[15] !== paginaPendente || $[16] !== pedirAoAproximar || $[17] !== sentinela) {
    t5 = () => {
      if (!sentinela || !paginaPendente) {
        return;
      }
      if (typeof IntersectionObserver === "undefined") {
        pedirAoAproximar();
        return;
      }
      const observer = new IntersectionObserver(entradas => {
        if (entradas.some(_temp)) {
          pedirAoAproximar();
        }
      }, {
        rootMargin: ANTECEDENCIA_DA_SENTINELA
      });
      observer.observe(sentinela);
      return () => observer.disconnect();
    };
    $[15] = paginaPendente;
    $[16] = pedirAoAproximar;
    $[17] = sentinela;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== paginaPendente || $[20] !== sentinela) {
    t6 = [sentinela, paginaPendente];
    $[19] = paginaPendente;
    $[20] = sentinela;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  useEffect(t5, t6);
  let t7;
  if ($[22] !== carregamento || $[23] !== documento || $[24] !== pedirProximaPagina) {
    t7 = {
      documento,
      carregamento,
      sentinelaRef: setSentinela,
      tentarNovamente: pedirProximaPagina
    };
    $[22] = carregamento;
    $[23] = documento;
    $[24] = pedirProximaPagina;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
}
function _temp(entrada) {
  return entrada.isIntersecting;
}